import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getSales, getOtherCommissions, createOtherCommission, updateOtherCommission, deleteOtherCommission, bulkUpdateCommissionsSnapshot, bulkUpdateSalesSnapshot, getCustomFields, getProjects, getActivityTypes, getUsers, getAllUsersForDisplay, getUsersAvailableForEntry, getContracts, getBusinessUnits, getProducts } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { MultiSelect } from '../components/ui/multi-select';
import { Combobox } from '../components/ui/combobox';
import { SearchableCheckboxList } from '../components/ui/searchable-checkbox-list';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, MoreHorizontal, Pencil, Trash2, Search, Loader2, TrendingUp, CircleDollarSign, ShoppingCart, Briefcase, Calendar, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown, CheckSquare, X, Columns3, ExternalLink } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { CustomFieldsSection, CustomFieldInput, formatCustomFieldDisplay, CustomFieldFilter, matchesCustomFieldFilter, activeCustomFieldFilters } from '../components/EntityCustomFields';
import { format, parseISO, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, subQuarters, startOfYear, endOfYear, subYears } from 'date-fns';

// Date presets
const DATE_PRESETS = [
  { 
    value: 'this_month', 
    label: 'Questo mese',
    getValue: () => ({
      start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
    })
  },
  { 
    value: 'last_month', 
    label: 'Mese scorso',
    getValue: () => ({
      start: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
      end: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd')
    })
  },
  { 
    value: 'this_quarter', 
    label: 'Questo trimestre',
    getValue: () => ({
      start: format(startOfQuarter(new Date()), 'yyyy-MM-dd'),
      end: format(endOfQuarter(new Date()), 'yyyy-MM-dd')
    })
  },
  { 
    value: 'last_quarter', 
    label: 'Trimestre scorso',
    getValue: () => ({
      start: format(startOfQuarter(subQuarters(new Date(), 1)), 'yyyy-MM-dd'),
      end: format(endOfQuarter(subQuarters(new Date(), 1)), 'yyyy-MM-dd')
    })
  },
  { 
    value: 'this_year', 
    label: "Quest'anno",
    getValue: () => ({
      start: format(startOfYear(new Date()), 'yyyy-MM-dd'),
      end: format(endOfYear(new Date()), 'yyyy-MM-dd')
    })
  },
  { 
    value: 'last_year', 
    label: 'Anno scorso',
    getValue: () => ({
      start: format(startOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd'),
      end: format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd')
    })
  },
];

const BASE_COLUMNS = [
  { key: 'date', label: 'Data', locked: true },
  { key: 'type', label: 'Tipologia' },
  { key: 'project', label: 'Progetto' },
  { key: 'product', label: 'Prodotto' },
  { key: 'bu', label: 'BU' },
  { key: 'user', label: 'Utente' },
  { key: 'pm', label: 'Project Manager' },
  { key: 'supervisor', label: 'Supervisor' },
  { key: 'contract', label: 'Contratto' },
  { key: 'qty', label: 'QTY' },
  { key: 'commission', label: 'Success Fee', locked: true },
];
const COLUMNS_STORAGE_KEY = 'commissions_columns_v1';
const selKey = (c) => `${c.type}:${c.id}`;

const formatCurrency = (value) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);

export default function CommissionsPage() {
  const { user, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight') || null;
  const [sales, setSales] = useState([]);
  const [otherCommissions, setOtherCommissions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [venditori, setVenditori] = useState([]); // Tutti gli utenti (per display storico)
  const [availableVenditori, setAvailableVenditori] = useState([]); // Utenti selezionabili per inserimento
  const [pmUsers, setPmUsers] = useState([]); // Lista PM per selezione
  const [supervisorUsers, setSupervisorUsers] = useState([]); // Lista Supervisor per selezione
  const [contracts, setContracts] = useState([]); // Lista Contratti per snapshot
  const [products, setProducts] = useState([]); // Lista Prodotti per snapshot
  const [businessUnits, setBusinessUnits] = useState([]); // Lista BU per filtro
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtri avanzati
  const [filterProjects, setFilterProjects] = useState([]); // Selezione multipla
  const [filterTypes, setFilterTypes] = useState([]); // Selezione multipla
  const [filterVenditori, setFilterVenditori] = useState([]); // Operativi - selezione multipla
  const [filterPMs, setFilterPMs] = useState([]); // Project Manager - selezione multipla
  const [filterBUs, setFilterBUs] = useState([]); // Business Unit - selezione multipla
  const [filterProducts, setFilterProducts] = useState([]); // Prodotti - selezione multipla
  const [datePreset, setDatePreset] = useState(''); // Preset data selezionato
  const [dateRange, setDateRange] = useState({ start: '', end: '' }); // Range date personalizzato
  
  // Ordinamento
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [sfCustomFields, setSfCustomFields] = useState([]);
  const [cfFilters, setCfFilters] = useState({});
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY)) || {}; } catch { return {}; }
  });
  const isColVisible = (key) => (key.startsWith('cf_') ? visibleColumns[key] === true : visibleColumns[key] !== false);
  const toggleColumn = (key) => {
    const next = { ...visibleColumns, [key]: !isColVisible(key) };
    setVisibleColumns(next);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
  };
  const visibleCfColumns = sfCustomFields.filter(f => isColVisible(`cf_${f.id}`));

  // Scroll into view + highlight row when ?highlight=<id> arrives
  useEffect(() => {
    if (!highlightId || loading) return;
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(`comm-row-${highlightId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
    const clearTimer = setTimeout(() => {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.delete('highlight');
        return params;
      }, { replace: true });
    }, 6000);
    return () => { clearTimeout(scrollTimer); clearTimeout(clearTimer); };
  }, [highlightId, loading, setSearchParams]);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    pm_ids: null,
    supervisor_ids: null,
    contract_id: null,
    product_id: null,
    custom_fields: {},
  });

  const [formData, setFormData] = useState({
    project_id: '',
    activity_type_id: '',
    description: '',
    activity_date: new Date().toISOString().split('T')[0],
    commission_type: 'fixed',
    commission_value: '',
    quantity: '1',
    venditore_id: '', // Per admin/project_manager
    pm_ids: [], // Per admin - selezione PM
    supervisor_ids: [], // Per admin - selezione Supervisor
    product_id: '', // Per admin - selezione Prodotto snapshot
    contract_ids: [], // Per admin - selezione Contratti snapshot
    custom_fields: {},
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [salesRes, otherRes, projectsRes, typesRes, contractsRes, buRes, productsRes] = await Promise.all([
        getSales(),
        getOtherCommissions(),
        getProjects(),
        getActivityTypes(),
        getContracts(),
        getBusinessUnits(),
        getProducts(),
      ]);
      setSales(salesRes.data);
      setOtherCommissions(otherRes.data);
      setContracts(contractsRes.data);
      setProducts(productsRes.data);
      setProjects(projectsRes.data);
      setActivityTypes(typesRes.data);
      setBusinessUnits(buRes.data);
      getCustomFields('success_fee').then(r => setSfCustomFields(r.data || [])).catch(() => {});

      // Carica TUTTI gli utenti (inclusi disattivati) per mostrare i nomi corretti
      // Questo è necessario per la risoluzione dei nomi nella cronologia
      const allUsersRes = await getAllUsersForDisplay();
      const allUsers = allUsersRes.data;
      
      // Aggiungi l'utente corrente alla lista se non è già presente
      if (!allUsers.find(u => u.id === user.id)) {
        allUsers.push({
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
          is_active: true
        });
      }
      
      setVenditori(allUsers);
      
      // Carica gli utenti disponibili per l'inserimento (filtrati per ruolo)
      const availableRes = await getUsersAvailableForEntry();
      setAvailableVenditori(availableRes.data);
      
      // Filtra solo i PM attivi per la selezione
      const activePMs = allUsers.filter(u => 
        (u.role === 'project_manager' || (u.roles && u.roles.includes('project_manager'))) && 
        u.is_active !== false
      );
      setPmUsers(activePMs);
      
      // Filtra i Supervisor attivi per la selezione
      const activeSupervisors = allUsers.filter(u => 
        (u.role === 'supervisor' || (u.roles && u.roles.includes('supervisor'))) && 
        u.is_active !== false
      );
      setSupervisorUsers(activeSupervisors);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare i dati', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Combine all commissions into a unified list
  const allCommissions = [
    // Sales commissions
    ...sales.map(s => ({
      id: s.id,
      type: 'sale',
      type_name: 'Vendite',
      project_id: s.project_id,
      date: s.sale_date,
      description: s.description,
      amount: s.amount,
      commission: s.calculated_commission,
      quantity: 1,
      venditore_id: s.venditore_id,
      pm_ids_snapshot: s.pm_ids_snapshot || [],
      supervisor_ids_snapshot: s.supervisor_ids_snapshot || [],
      contract_ids_snapshot: s.contract_ids_snapshot || [],
      contract_id_snapshot: s.contract_id_snapshot,
      contract_name_snapshot: s.contract_name_snapshot,
      product_name_snapshot: s.product_name_snapshot,
      business_unit_name_snapshot: s.business_unit_name_snapshot,
      missing_contract: s.missing_contract,
      custom_fields: s.custom_fields || {},
    })),
    // Other commissions
    ...otherCommissions.map(c => ({
      id: c.id,
      type: 'other',
      type_id: c.activity_type_id,
      type_name: activityTypes.find(t => t.id === c.activity_type_id)?.name || 'N/A',
      project_id: c.project_id,
      date: c.activity_date,
      description: c.description,
      amount: null,
      commission: c.calculated_commission,
      quantity: c.quantity,
      venditore_id: c.venditore_id,
      pm_ids_snapshot: c.pm_ids_snapshot || [],
      supervisor_ids_snapshot: c.supervisor_ids_snapshot || [],
      product_id: c.product_id_snapshot || '',
      contract_ids_snapshot: c.contract_ids_snapshot || [],
      contract_id_snapshot: c.contract_id_snapshot,
      contract_name_snapshot: c.contract_name_snapshot,
      product_name_snapshot: c.product_name_snapshot,
      business_unit_name_snapshot: c.business_unit_name_snapshot,
      missing_contract: c.missing_contract,
      custom_fields: c.custom_fields || {},
      original: c,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  const handleOpenDialog = (commission = null) => {
    if (commission && commission.type === 'other') {
      const orig = commission.original;
      setEditingCommission(orig);
      
      // Gestione contract_ids: usa contract_ids_snapshot se non vuoto, altrimenti fallback a contract_id_snapshot
      let contractIds = [];
      if (orig.contract_ids_snapshot && orig.contract_ids_snapshot.length > 0) {
        contractIds = orig.contract_ids_snapshot;
      } else if (orig.contract_id_snapshot) {
        contractIds = [orig.contract_id_snapshot];
      }
      
      setFormData({
        project_id: orig.project_id,
        activity_type_id: orig.activity_type_id,
        description: orig.description || '',
        activity_date: orig.activity_date?.split('T')[0] || '',
        commission_type: orig.commission_type || 'fixed',
        commission_value: orig.commission_value?.toString() || '',
        quantity: orig.quantity?.toString() || '1',
        venditore_id: orig.venditore_id || '',
        pm_ids: orig.pm_ids_snapshot || [],
        supervisor_ids: orig.supervisor_ids_snapshot || [],
        product_id: orig.product_id_snapshot || '',
        contract_ids: contractIds,
        custom_fields: orig.custom_fields || {},
      });
    } else {
      setEditingCommission(null);
      setFormData({
        project_id: '',
        activity_type_id: '',
        description: '',
        activity_date: new Date().toISOString().split('T')[0],
        commission_type: 'fixed',
        commission_value: '',
        quantity: '1',
        venditore_id: '',
        pm_ids: [],
        supervisor_ids: [],
        product_id: '',
        contract_ids: [],
        custom_fields: {},
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missingCF = sfCustomFields.filter(f => f.is_required && [undefined, null, ''].includes(formData.custom_fields?.[f.id]));
    if (missingCF.length) {
      toast({ title: 'Campi obbligatori mancanti', description: missingCF.map(f => f.name).join(', '), variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const data = {
        custom_fields: formData.custom_fields || {},
        project_id: formData.project_id,
        activity_type_id: formData.activity_type_id,
        description: formData.description || null,
        activity_date: new Date(formData.activity_date).toISOString(),
        commission_type: formData.commission_type,
        commission_value: parseFloat(formData.commission_value),
        quantity: parseInt(formData.quantity) || 1,
      };

      // Per update, includi anche project_id
      if (editingCommission) {
        data.project_id = formData.project_id;
      }

      // Aggiungi venditore_id solo per admin/HR/project_manager
      if (hasRole(['admin', 'hr', 'project_manager']) && formData.venditore_id) {
        data.venditore_id = formData.venditore_id;
      }

      // Admin/HR può modificare i PM (in modifica)
      if (hasRole(['admin', 'hr']) && editingCommission) {
        data.pm_ids_snapshot = formData.pm_ids;
      }

      // Admin/HR può modificare i Supervisor (in modifica)
      if (hasRole(['admin', 'hr']) && editingCommission) {
        data.supervisor_ids_snapshot = formData.supervisor_ids;
      }

      // Admin/HR può modificare il Prodotto snapshot (in modifica)
      if (hasRole(['admin', 'hr']) && editingCommission && formData.product_id) {
        data.product_id_snapshot = formData.product_id;
      }

      // Admin/HR può modificare il Contratto snapshot (in modifica)
      // Usa il primo contratto selezionato da contract_ids array
      if (hasRole(['admin', 'hr']) && editingCommission) {
        const selectedContract = formData.contract_ids && formData.contract_ids.length > 0 
          ? formData.contract_ids[0] 
          : null;
        if (selectedContract) {
          data.contract_id_snapshot = selectedContract;
        }
      }

      if (editingCommission) {
        await updateOtherCommission(editingCommission.id, data);
        toast({ title: 'Success Fee aggiornato' });
      } else {
        await createOtherCommission(data);
        toast({ title: 'Success Fee registrato' });
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (commission) => {
    if (commission.type === 'sale') {
      toast({ title: 'Info', description: 'Le vendite si eliminano dalla sezione Vendite', variant: 'default' });
      return;
    }
    if (!window.confirm('Eliminare questo Success Fee?')) return;
    try {
      await deleteOtherCommission(commission.id);
      toast({ title: 'Success Fee eliminato' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile eliminare il Success Fee', variant: 'destructive' });
    }
  };

  // Bulk selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allKeys = sortedCommissions.map(selKey);
    if (selectedIds.size === allKeys.length && allKeys.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allKeys));
    }
  };

  const openBulkDialog = () => {
    setBulkFormData({ pm_ids: null, supervisor_ids: null, contract_id: null, product_id: null, custom_fields: {} });
    setBulkDialogOpen(true);
  };

  const bulkHasChanges = bulkFormData.pm_ids !== null || bulkFormData.supervisor_ids !== null || !!bulkFormData.contract_id || !!bulkFormData.product_id || Object.keys(bulkFormData.custom_fields || {}).length > 0;

  const handleBulkUpdate = async () => {
    setBulkSaving(true);
    try {
      const payload = {};
      if (bulkFormData.pm_ids !== null) payload.pm_ids_snapshot = bulkFormData.pm_ids;
      if (bulkFormData.supervisor_ids !== null) payload.supervisor_ids_snapshot = bulkFormData.supervisor_ids;
      if (bulkFormData.contract_id) payload.contract_id_snapshot = bulkFormData.contract_id;
      if (bulkFormData.product_id) payload.product_id_snapshot = bulkFormData.product_id;
      if (Object.keys(bulkFormData.custom_fields || {}).length) payload.custom_fields = bulkFormData.custom_fields;

      const keys = Array.from(selectedIds);
      const saleIds = keys.filter(k => k.startsWith('sale:')).map(k => k.slice(5));
      const otherIds = keys.filter(k => k.startsWith('other:')).map(k => k.slice(6));
      const results = await Promise.allSettled([
        saleIds.length ? bulkUpdateSalesSnapshot({ ...payload, ids: saleIds }) : null,
        otherIds.length ? bulkUpdateCommissionsSnapshot({ ...payload, ids: otherIds }) : null,
      ]);
      const updated = results.reduce((n, r) => n + (r.status === 'fulfilled' ? (r.value?.data?.updated || 0) : 0), 0);
      const failed = results.map((r, i) => (r.status === 'rejected' ? (i === 0 ? 'vendite' : 'success fee') : null)).filter(Boolean);
      if (failed.length) {
        toast({ title: `Aggiornamento parziale: ${updated} elementi aggiornati`, description: `Errore sulle ${failed.join(' e ')}`, variant: 'destructive' });
      } else {
        toast({ title: `${updated} elementi aggiornati`, description: `${saleIds.length} vendite, ${otherIds.length} success fee selezionate` });
      }
      setSelectedIds(new Set());
      setBulkDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore aggiornamento massivo', variant: 'destructive' });
    } finally {
      setBulkSaving(false);
    }
  };

  const getProjectName = (projectId) => projects.find((p) => p.id === projectId)?.name || 'N/A';
  
  const getVenditore = (venditoreId) => {
    if (!venditoreId) return 'N/A';
    const venditore = venditori.find((v) => v.id === venditoreId);
    if (!venditore) return '(Utente non trovato)';
    return venditore.is_active === false ? `${venditore.name} (disattivato)` : venditore.name;
  };
  
  // Funzione per ottenere i nomi dei PM dallo snapshot
  const getPmNamesFromSnapshot = (pmIdsSnapshot) => {
    if (!pmIdsSnapshot || pmIdsSnapshot.length === 0) return '-';
    const pmNames = pmIdsSnapshot.map(pmId => {
      const pm = venditori.find(v => v.id === pmId);
      if (!pm) return '(PM non trovato)';
      return pm.is_active === false ? `${pm.name} (disattivato)` : pm.name;
    });
    return pmNames.join(', ');
  };

  // Funzione per ottenere i nomi dei Supervisor dallo snapshot
  const getSupervisorNamesFromSnapshot = (supervisorIdsSnapshot) => {
    if (!supervisorIdsSnapshot || supervisorIdsSnapshot.length === 0) return '-';
    const supNames = supervisorIdsSnapshot.map(supId => {
      const sup = venditori.find(v => v.id === supId);
      if (!sup) return '(Supervisor non trovato)';
      return sup.is_active === false ? `${sup.name} (disattivato)` : sup.name;
    });
    return supNames.join(', ');
  };

  // Funzione per ottenere i nomi dei Contratti dallo snapshot
  const getContractNamesFromSnapshot = (comm) => {
    // Usa il nuovo campo singolo contract_id_snapshot
    if (comm.contract_id_snapshot) {
      const contract = contracts.find(c => c.id === comm.contract_id_snapshot);
      return contract?.name || comm.contract_name_snapshot || '(Contratto non trovato)';
    }
    return null; // Nessun contratto associato
  };

  // Funzione per ottenere info prodotto
  const getProductInfo = (comm) => {
    if (comm.product_name_snapshot) {
      return {
        name: comm.product_name_snapshot,
        bu: comm.business_unit_name_snapshot || '-'
      };
    }
    return null;
  };
  
  // Filter out "Vendite" from types available for manual entry
  const availableTypes = activityTypes.filter(t => t.name !== 'Vendite');

  // Handle date preset change
  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset && preset !== 'custom') {
      const presetConfig = DATE_PRESETS.find(p => p.value === preset);
      if (presetConfig) {
        setDateRange(presetConfig.getValue());
      }
    } else if (preset === 'custom') {
      // Keep current custom range
    } else {
      setDateRange({ start: '', end: '' });
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterProjects([]);
    setFilterTypes([]);
    setFilterVenditori([]);
    setFilterPMs([]);
    setFilterBUs([]);
    setFilterProducts([]);
    setDatePreset('');
    setDateRange({ start: '', end: '' });
    setCfFilters({});
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm || filterProjects.length > 0 || filterTypes.length > 0 || 
    filterVenditori.length > 0 || filterPMs.length > 0 || filterBUs.length > 0 || filterProducts.length > 0 || dateRange.start || dateRange.end || activeCustomFieldFilters(cfFilters).length > 0;

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const filteredCommissions = useMemo(() => {
    return allCommissions.filter((c) => {
      // Search filter
      const matchesSearch = !searchTerm || 
                            c.description?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            getProjectName(c.project_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
                            c.type_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            getVenditore(c.venditore_id).toLowerCase().includes(searchTerm.toLowerCase());
      
      // Projects filter (multi-select)
      const matchesProject = filterProjects.length === 0 || filterProjects.includes(c.project_id);
      
      // Type filter (multi-select)
      const matchesType = filterTypes.length === 0 || 
                          (filterTypes.includes('sales') && c.type === 'sale') ||
                          (c.type === 'other' && filterTypes.includes(c.type_id));
      
      // Operativo/Venditore filter (multi-select)
      const matchesVenditore = filterVenditori.length === 0 || filterVenditori.includes(c.venditore_id);
      
      // PM filter (multi-select) - check pm_ids_snapshot
      const matchesPM = filterPMs.length === 0 || 
                        (c.pm_ids_snapshot && c.pm_ids_snapshot.some(pmId => filterPMs.includes(pmId)));
      
      // BU filter (multi-select) - check business_unit_name_snapshot
      const matchesBU = filterBUs.length === 0 || 
                        (c.business_unit_name_snapshot && filterBUs.some(buId => {
                          const bu = businessUnits.find(b => b.id === buId);
                          return bu && bu.name === c.business_unit_name_snapshot;
                        }));
      
      // Product filter (multi-select) - check product_id_snapshot
      const matchesProduct = filterProducts.length === 0 ||
                             (c.product_id_snapshot && filterProducts.includes(c.product_id_snapshot));
      
      // Date filter
      let matchesDate = true;
      if (dateRange.start || dateRange.end) {
        const commDate = c.date ? new Date(c.date) : null;
        if (commDate) {
          const startDate = dateRange.start ? new Date(dateRange.start) : new Date('1900-01-01');
          const endDate = dateRange.end ? new Date(dateRange.end) : new Date('2100-12-31');
          // Set end of day for end date
          endDate.setHours(23, 59, 59, 999);
          matchesDate = commDate >= startDate && commDate <= endDate;
        }
      }
      
      const matchesCF = sfCustomFields.every(f => matchesCustomFieldFilter(f, c.custom_fields?.[f.id], cfFilters[f.id]));

      return matchesSearch && matchesProject && matchesType && matchesVenditore && matchesPM && matchesBU && matchesProduct && matchesDate && matchesCF;
    });
  }, [allCommissions, searchTerm, filterProjects, filterTypes, filterVenditori, filterPMs, filterBUs, filterProducts, dateRange, businessUnits, sfCustomFields, cfFilters]);

  // Sort filtered commissions
  const sortedCommissions = useMemo(() => {
    return [...filteredCommissions].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'date':
          aValue = a.date ? new Date(a.date) : new Date(0);
          bValue = b.date ? new Date(b.date) : new Date(0);
          break;
        case 'type':
          aValue = a.type_name?.toLowerCase() || '';
          bValue = b.type_name?.toLowerCase() || '';
          break;
        case 'project':
          aValue = getProjectName(a.project_id)?.toLowerCase() || '';
          bValue = getProjectName(b.project_id)?.toLowerCase() || '';
          break;
        case 'venditore':
          aValue = getVenditore(a.venditore_id)?.toLowerCase() || '';
          bValue = getVenditore(b.venditore_id)?.toLowerCase() || '';
          break;
        case 'commission':
          aValue = a.commission || 0;
          bValue = b.commission || 0;
          break;
        default:
          aValue = a.date ? new Date(a.date) : new Date(0);
          bValue = b.date ? new Date(b.date) : new Date(0);
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [filteredCommissions, sortColumn, sortDirection]);

  const totalCommissions = filteredCommissions.reduce((sum, c) => sum + c.commission, 0);
  const salesTotal = filteredCommissions.filter(c => c.type === 'sale').reduce((sum, c) => sum + c.commission, 0);
  const otherTotal = filteredCommissions.filter(c => c.type === 'other').reduce((sum, c) => sum + c.commission, 0);

  // Get projects where user is assigned (for venditori)
  // Backend already filters projects based on user role via project_users collection
  // So we just use the projects returned by the API
  const availableProjects = projects;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Success Fee</h1>
          <p className="text-gray-500">Tutti i Success Fee (Vendite + Altre attività)</p>
        </div>
        {hasRole(['admin', 'hr', 'venditore', 'project_manager']) && availableTypes.length > 0 && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-commission-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Success Fee
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Totale Success Fee</CardTitle>
            <CircleDollarSign className="w-4 h-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCommissions)}</div>
            <p className="text-xs text-gray-500">{filteredCommissions.length} attività</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Da Vendite</CardTitle>
            <ShoppingCart className="w-4 h-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(salesTotal)}</div>
            <p className="text-xs text-gray-500">{filteredCommissions.filter(c => c.type === 'sale').length} vendite</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Da Altre Attività</CardTitle>
            <Briefcase className="w-4 h-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(otherTotal)}</div>
            <p className="text-xs text-gray-500">{filteredCommissions.filter(c => c.type === 'other').length} attività</p>
          </CardContent>
        </Card>
      </div>

      {/* Bulk actions bar */}
      {hasRole(['admin']) && selectedIds.size > 0 && (
        <div className="sticky top-0 z-20 bg-blue-600 text-white rounded-lg p-3 flex items-center justify-between shadow-lg" data-testid="bulk-actions-bar-sf">
          <div className="flex items-center gap-4">
            <CheckSquare className="w-5 h-5" />
            <span className="font-medium" data-testid="bulk-selected-count">{selectedIds.size} element{selectedIds.size === 1 ? 'o selezionato' : 'i selezionati'}</span>
            <span className="text-blue-200">|</span>
            <span className="text-sm">Totale QTY: <strong>{sortedCommissions.filter(c => selectedIds.has(selKey(c))).reduce((s, c) => s + (c.quantity || 1), 0)}</strong></span>
            <span className="text-blue-200">|</span>
            <span className="text-sm">Totale: <strong>{formatCurrency(sortedCommissions.filter(c => selectedIds.has(selKey(c))).reduce((s, c) => s + (c.commission || 0), 0))}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={openBulkDialog} data-testid="bulk-edit-sf-btn">
              <Pencil className="w-4 h-4 mr-1" />
              Modifica Massiva
            </Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-blue-700" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear-selection">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          {/* Prima riga: Ricerca e Filtri principali */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Cerca..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Filtro Data con Preset */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <Select value={datePreset || "all"} onValueChange={(v) => handleDatePresetChange(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Periodo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutto il periodo</SelectItem>
                    {DATE_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>
                    ))}
                    <SelectItem value="custom">Personalizzato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Date Range personalizzato */}
              {datePreset === 'custom' && (
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="w-[140px]"
                  />
                  <span className="text-gray-400">-</span>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="w-[140px]"
                  />
                </div>
              )}
              
              {/* Reset filters */}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="text-gray-500">
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reset
                </Button>
              )}
            </div>
            
            <div className="flex justify-end -mt-2">
              <div className="relative">
                <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => setColMenuOpen(!colMenuOpen)} data-testid="sf-col-config-btn">
                  <Columns3 className="w-3 h-3" /> Colonne
                </Button>
                {colMenuOpen && (
                  <div className="absolute right-0 top-9 z-50 bg-white border rounded-lg shadow-lg p-3 min-w-[220px] max-h-[60vh] overflow-y-auto" data-testid="sf-col-config-menu">
                    <div className="text-[11px] font-medium text-gray-400 uppercase mb-2">Colonne visibili</div>
                    {BASE_COLUMNS.map(col => (
                      <label key={col.key} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                        <Checkbox checked={col.locked || isColVisible(col.key)} disabled={col.locked}
                          onCheckedChange={() => !col.locked && toggleColumn(col.key)} data-testid={`sf-col-toggle-${col.key}`} />
                        {col.label}
                      </label>
                    ))}
                    {sfCustomFields.length > 0 && (
                      <div className="text-[11px] font-medium text-gray-400 uppercase mt-3 mb-2">Campi personalizzati</div>
                    )}
                    {sfCustomFields.map(f => (
                      <label key={f.id} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                        <Checkbox checked={isColVisible(`cf_${f.id}`)} onCheckedChange={() => toggleColumn(`cf_${f.id}`)} data-testid={`sf-col-toggle-cf-${f.id}`} />
                        {f.name}
                      </label>
                    ))}
                    <Button variant="ghost" size="sm" className="w-full mt-2 text-xs" onClick={() => setColMenuOpen(false)}>Chiudi</Button>
                  </div>
                )}
              </div>
            </div>

            {/* Seconda riga: Filtri Multi-select */}
            <div className="flex flex-wrap gap-3">
              {/* Progetti - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={projects.map(p => ({ value: p.id, label: p.name }))}
                  selected={filterProjects}
                  onChange={setFilterProjects}
                  placeholder="Progetti"
                />
              </div>
              
              {/* Tipologia - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={[
                    { value: 'sales', label: 'Vendite' },
                    ...availableTypes.map(t => ({ value: t.id, label: t.name }))
                  ]}
                  selected={filterTypes}
                  onChange={setFilterTypes}
                  placeholder="Tipologia"
                />
              </div>
              
              {/* Operativo - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={venditori.filter(v => v.is_active !== false).map(v => ({ value: v.id, label: v.name }))}
                  selected={filterVenditori}
                  onChange={setFilterVenditori}
                  placeholder="Operativo"
                />
              </div>
              
              {/* Project Manager - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={pmUsers.map(pm => ({ value: pm.id, label: pm.name }))}
                  selected={filterPMs}
                  onChange={setFilterPMs}
                  placeholder="Project Manager"
                />
              </div>
              
              {/* BU - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={businessUnits.map(bu => ({ value: bu.id, label: bu.name }))}
                  selected={filterBUs}
                  onChange={setFilterBUs}
                  placeholder="Business Unit"
                />
              </div>
              
              {/* Prodotti - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={products.filter(p => p.is_active !== false).map(p => ({ value: p.id, label: p.name }))}
                  selected={filterProducts}
                  onChange={setFilterProducts}
                  placeholder="Prodotti"
                />
              </div>
            </div>

            {sfCustomFields.length > 0 && (
              <div className="flex flex-wrap items-center gap-3" data-testid="sf-cf-filters">
                <span className="text-xs font-medium text-gray-400 uppercase">Campi personalizzati</span>
                {sfCustomFields.map(f => (
                  <CustomFieldFilter
                    key={f.id}
                    field={f}
                    value={cfFilters[f.id]}
                    onChange={(v) => setCfFilters(prev => ({ ...prev, [f.id]: v }))}
                  />
                ))}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredCommissions.length === 0 ? (
            <div className="text-center py-12">
              <CircleDollarSign className="w-12 h-12 mx-auto text-gray-300" />
              <p className="mt-4 text-gray-500">Nessun Success Fee trovato</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {hasRole(['admin']) && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={sortedCommissions.length > 0 && selectedIds.size === sortedCommissions.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-sf"
                      />
                    </TableHead>
                  )}
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center">
                      Data
                      {getSortIcon('date')}
                    </div>
                  </TableHead>
                  {isColVisible('type') && <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center">
                      Tipologia
                      {getSortIcon('type')}
                    </div>
                  </TableHead>}
                  {isColVisible('project') && <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('project')}
                  >
                    <div className="flex items-center">
                      Progetto
                      {getSortIcon('project')}
                    </div>
                  </TableHead>}
                  {isColVisible('product') && <TableHead>Prodotto</TableHead>}
                  {isColVisible('bu') && <TableHead>BU</TableHead>}
                  {isColVisible('user') && <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('venditore')}
                  >
                    <div className="flex items-center">
                      Utente
                      {getSortIcon('venditore')}
                    </div>
                  </TableHead>}
                  {isColVisible('pm') && <TableHead>Project Manager</TableHead>}
                  {isColVisible('supervisor') && <TableHead>Supervisor</TableHead>}
                  {isColVisible('contract') && <TableHead>Contratto</TableHead>}
                  {isColVisible('qty') && <TableHead>QTY</TableHead>}
                  {visibleCfColumns.map(f => <TableHead key={f.id} data-testid={`sf-col-head-cf-${f.id}`}>{f.name}</TableHead>)}
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('commission')}
                  >
                    <div className="flex items-center">
                      Success Fee
                      {getSortIcon('commission')}
                    </div>
                  </TableHead>
                  {hasRole(['admin', 'hr', 'project_manager', 'venditore']) && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCommissions.map((comm) => {
                  const productInfo = getProductInfo(comm);
                  const contractName = getContractNamesFromSnapshot(comm);
                  const isMissingContract = comm.missing_contract || !contractName;
                  
                  return (
                    <TableRow
                      key={`${comm.type}-${comm.id}`}
                      id={`comm-row-${comm.id}`}
                      className={`${isMissingContract ? 'bg-amber-50' : ''} ${selectedIds.has(selKey(comm)) ? 'bg-blue-50' : ''} ${highlightId === comm.id ? 'ring-2 ring-orange-400 ring-inset bg-orange-50' : ''}`}
                    >
                      {hasRole(['admin']) && (
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(selKey(comm))}
                            onCheckedChange={() => toggleSelect(selKey(comm))}
                            data-testid={`select-row-${comm.type}-${comm.id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        {comm.date ? format(parseISO(comm.date), 'dd/MM/yyyy') : '-'}
                      </TableCell>
                      {isColVisible('type') && <TableCell>
                        <Badge variant={comm.type === 'sale' ? 'default' : 'outline'}>
                          {comm.type === 'sale' ? <ShoppingCart className="w-3 h-3 mr-1" /> : <Briefcase className="w-3 h-3 mr-1" />}
                          {comm.type_name}
                        </Badge>
                      </TableCell>}
                      {isColVisible('project') && <TableCell className="font-medium">{getProjectName(comm.project_id)}</TableCell>}
                      {isColVisible('product') && <TableCell>
                        {productInfo ? (
                          <span className="text-sm">{productInfo.name}</span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>}
                      {isColVisible('bu') && <TableCell>
                        {productInfo?.bu ? (
                          <Badge variant="outline" className="text-xs">{productInfo.bu}</Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>}
                      {isColVisible('user') && <TableCell>{getVenditore(comm.venditore_id)}</TableCell>}
                      {isColVisible('pm') && <TableCell className="text-purple-600">
                        {getPmNamesFromSnapshot(comm.pm_ids_snapshot)}
                      </TableCell>}
                      {isColVisible('supervisor') && <TableCell className="text-indigo-600">
                        {getSupervisorNamesFromSnapshot(comm.supervisor_ids_snapshot)}
                      </TableCell>}
                      {isColVisible('contract') && <TableCell>
                        {contractName ? (
                          <span className="text-blue-600">{contractName}</span>
                        ) : (
                          <span className="text-amber-600 flex items-center gap-1">
                            <span>⚠️</span>
                            <span className="text-xs">Nessun contratto</span>
                          </span>
                        )}
                      </TableCell>}
                      {isColVisible('qty') && <TableCell className="text-center">{comm.quantity || 1}</TableCell>}
                      {visibleCfColumns.map(f => (
                        <TableCell key={f.id} className="text-sm" data-testid={`sf-cell-cf-${f.id}-${comm.id}`}>
                          {formatCustomFieldDisplay(f, comm.custom_fields?.[f.id]) ?? <span className="text-gray-400">-</span>}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Badge className="bg-green-100 text-green-700">
                          {formatCurrency(comm.commission)}
                        </Badge>
                      </TableCell>
                      {hasRole(['admin', 'hr', 'project_manager', 'venditore']) && (
                        <TableCell>
                          {comm.type === 'sale' && (
                            <Button variant="ghost" size="icon" title="Apri in Vendite" onClick={() => navigate(`/sales?highlight=${comm.id}`)} data-testid={`open-sale-${comm.id}`}>
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                          {comm.type === 'other' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleOpenDialog(comm)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Modifica
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDelete(comm)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Elimina
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCommission ? 'Modifica Success Fee' : 'Nuovo Success Fee'}</DialogTitle>
            <DialogDescription>
              {editingCommission ? 'Modifica i dati' : 'Registra un nuovo Success Fee (non da vendita)'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Campo Utente - solo per admin/HR/project_manager */}
            {hasRole(['admin', 'hr', 'project_manager']) && (
              <div className="space-y-2">
                <Label>Utente</Label>
                <Combobox
                  options={[
                    { value: 'me', label: 'Me stesso' },
                    ...availableVenditori.filter(v => v.is_active !== false).map((v) => ({
                      value: v.id,
                      label: `${v.name} (${v.email})`
                    }))
                  ]}
                  value={formData.venditore_id || 'me'}
                  onChange={(v) => setFormData({ ...formData, venditore_id: v === 'me' ? '' : v })}
                  placeholder="Seleziona utente"
                  searchPlaceholder="Cerca utente..."
                  emptyText="Nessun utente trovato"
                />
                {hasRole(['project_manager']) && !hasRole(['admin', 'hr']) && (
                  <p className="text-xs text-gray-500">
                    Puoi selezionare solo utenti assegnati ai tuoi progetti
                  </p>
                )}
              </div>
            )}

            {/* Campo Project Manager - solo Admin/HR in modifica */}
            {hasRole(['admin', 'hr']) && editingCommission && (
              <div className="space-y-2">
                <Label className="text-sm">Project Manager (snapshot)</Label>
                <SearchableCheckboxList
                  items={pmUsers}
                  selectedIds={formData.pm_ids}
                  onToggle={(id, checked) => {
                    if (checked) {
                      setFormData({ ...formData, pm_ids: [...formData.pm_ids, id] });
                    } else {
                      setFormData({ ...formData, pm_ids: formData.pm_ids.filter(i => i !== id) });
                    }
                  }}
                  idPrefix="pm-comm"
                  placeholder="Cerca PM..."
                  emptyText="Nessun PM disponibile"
                />
              </div>
            )}

            {/* Campo Supervisor - solo Admin/HR in modifica */}
            {hasRole(['admin', 'hr']) && editingCommission && (
              <div className="space-y-2">
                <Label className="text-sm">Supervisor (snapshot)</Label>
                <SearchableCheckboxList
                  items={supervisorUsers}
                  selectedIds={formData.supervisor_ids}
                  onToggle={(id, checked) => {
                    if (checked) {
                      setFormData({ ...formData, supervisor_ids: [...formData.supervisor_ids, id] });
                    } else {
                      setFormData({ ...formData, supervisor_ids: formData.supervisor_ids.filter(i => i !== id) });
                    }
                  }}
                  idPrefix="sup-comm"
                  placeholder="Cerca supervisor..."
                  emptyText="Nessun Supervisor disponibile"
                />
              </div>
            )}

            {/* Campo Prodotto - solo Admin/HR in modifica */}
            {hasRole(['admin', 'hr']) && editingCommission && (
              <div className="space-y-2">
                <Label className="text-sm">Prodotto (snapshot)</Label>
                <Combobox
                  options={products.map((p) => ({ value: p.id, label: p.name }))}
                  value={formData.product_id}
                  onChange={(val) => setFormData({ ...formData, product_id: val })}
                  placeholder="Seleziona prodotto"
                  searchPlaceholder="Cerca prodotto..."
                  emptyText="Nessun prodotto trovato"
                />
              </div>
            )}

            {/* Campo Contratti - solo Admin/HR in modifica */}
            {hasRole(['admin', 'hr']) && editingCommission && (
              <div className="space-y-2">
                <Label className="text-sm">Contratti (snapshot)</Label>
                <SearchableCheckboxList
                  items={contracts}
                  selectedIds={formData.contract_ids}
                  onToggle={(id, checked) => {
                    if (checked) {
                      setFormData({ ...formData, contract_ids: [...formData.contract_ids, id] });
                    } else {
                      setFormData({ ...formData, contract_ids: formData.contract_ids.filter(i => i !== id) });
                    }
                  }}
                  idPrefix="contract-comm"
                  placeholder="Cerca contratto..."
                  emptyText="Nessun contratto disponibile"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipologia Attività *</Label>
                <Select
                  value={formData.activity_type_id || "select"}
                  onValueChange={(v) => setFormData({ ...formData, activity_type_id: v === "select" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona tipologia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select" disabled>Seleziona tipologia</SelectItem>
                    {availableTypes.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Progetto *</Label>
                <Combobox
                  options={availableProjects.map(p => ({ value: p.id, label: p.name }))}
                  value={formData.project_id}
                  onChange={(v) => setFormData({ ...formData, project_id: v })}
                  placeholder="Seleziona progetto"
                  searchPlaceholder="Cerca progetto..."
                  emptyText="Nessun progetto trovato"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Attività *</Label>
                <Input
                  type="date"
                  value={formData.activity_date}
                  onChange={(e) => setFormData({ ...formData, activity_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Quantità *</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  onWheel={(e) => e.target.blur()}
                  required
                />
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
              <h4 className="font-medium">Success Fee per unità</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value="fixed"
                    disabled
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fisso (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Valore *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.commission_value}
                    onChange={(e) => setFormData({ ...formData, commission_value: e.target.value })}
                    onWheel={(e) => e.target.blur()}
                    placeholder="es. 50"
                    required
                  />
                </div>
              </div>
              {formData.quantity && formData.commission_value && (
                <p className="text-sm text-gray-600 pt-2 border-t">
                  Totale: {formData.commission_type === 'fixed' 
                    ? formatCurrency(parseFloat(formData.commission_value) * parseInt(formData.quantity || 1))
                    : `${parseFloat(formData.commission_value) * parseInt(formData.quantity || 1)}%`}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                placeholder="es. Appuntamento con cliente X"
              />
            </div>

            <CustomFieldsSection
              fields={sfCustomFields}
              values={formData.custom_fields}
              onChange={(cf) => setFormData(prev => ({ ...prev, custom_fields: cf }))}
              testIdPrefix="sf-cf"
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingCommission ? 'Salva Modifiche' : 'Registra'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Massiva</DialogTitle>
            <DialogDescription>
              Modifica {selectedIds.size} elementi ({Array.from(selectedIds).filter(k => k.startsWith('sale:')).length} vendite, {Array.from(selectedIds).filter(k => k.startsWith('other:')).length} success fee). Compila solo i campi da aggiornare.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Project Manager</Label>
                {bulkFormData.pm_ids !== null && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-500" onClick={() => setBulkFormData(p => ({...p, pm_ids: null}))}>
                    Annulla modifica
                  </Button>
                )}
              </div>
              {bulkFormData.pm_ids === null ? (
                <Button variant="outline" size="sm" className="w-full text-gray-500" onClick={() => setBulkFormData(p => ({...p, pm_ids: []}))}>
                  Clicca per modificare PM
                </Button>
              ) : (
                <SearchableCheckboxList
                  items={pmUsers}
                  selectedIds={bulkFormData.pm_ids}
                  onToggle={(id, checked) => {
                    setBulkFormData(p => ({
                      ...p,
                      pm_ids: checked ? [...(p.pm_ids || []), id] : (p.pm_ids || []).filter(i => i !== id)
                    }));
                  }}
                  idPrefix="bulk-pm-sf"
                  placeholder="Cerca PM..."
                  emptyText="Nessun PM disponibile"
                />
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Supervisor</Label>
                {bulkFormData.supervisor_ids !== null && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-500" onClick={() => setBulkFormData(p => ({...p, supervisor_ids: null}))}>
                    Annulla modifica
                  </Button>
                )}
              </div>
              {bulkFormData.supervisor_ids === null ? (
                <Button variant="outline" size="sm" className="w-full text-gray-500" onClick={() => setBulkFormData(p => ({...p, supervisor_ids: []}))}>
                  Clicca per modificare Supervisor
                </Button>
              ) : (
                <SearchableCheckboxList
                  items={supervisorUsers}
                  selectedIds={bulkFormData.supervisor_ids}
                  onToggle={(id, checked) => {
                    setBulkFormData(p => ({
                      ...p,
                      supervisor_ids: checked ? [...(p.supervisor_ids || []), id] : (p.supervisor_ids || []).filter(i => i !== id)
                    }));
                  }}
                  idPrefix="bulk-sup-sf"
                  placeholder="Cerca supervisor..."
                  emptyText="Nessun Supervisor disponibile"
                />
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Contratto</Label>
                {bulkFormData.contract_id !== null && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-500" onClick={() => setBulkFormData(p => ({...p, contract_id: null}))}>
                    Annulla modifica
                  </Button>
                )}
              </div>
              {bulkFormData.contract_id === null ? (
                <Button variant="outline" size="sm" className="w-full text-gray-500" onClick={() => setBulkFormData(p => ({...p, contract_id: ''}))}>
                  Clicca per modificare Contratto
                </Button>
              ) : (
                <Combobox
                  options={contracts.map(c => ({ value: c.id, label: c.name }))}
                  value={bulkFormData.contract_id}
                  onChange={(v) => setBulkFormData(p => ({...p, contract_id: v}))}
                  placeholder="Seleziona contratto"
                  searchPlaceholder="Cerca contratto..."
                  emptyText="Nessun contratto trovato"
                />
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Prodotto</Label>
                {bulkFormData.product_id !== null && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-500" onClick={() => setBulkFormData(p => ({...p, product_id: null}))}>
                    Annulla modifica
                  </Button>
                )}
              </div>
              {bulkFormData.product_id === null ? (
                <Button variant="outline" size="sm" className="w-full text-gray-500" onClick={() => setBulkFormData(p => ({...p, product_id: ''}))}>
                  Clicca per modificare Prodotto
                </Button>
              ) : (
                <Combobox
                  options={products.map(p => ({ value: p.id, label: p.name }))}
                  value={bulkFormData.product_id}
                  onChange={(v) => setBulkFormData(p => ({...p, product_id: v}))}
                  placeholder="Seleziona prodotto"
                  searchPlaceholder="Cerca prodotto..."
                  emptyText="Nessun prodotto trovato"
                />
              )}
            </div>

            {sfCustomFields.map(f => {
              const editing = Object.prototype.hasOwnProperty.call(bulkFormData.custom_fields || {}, f.id);
              return (
                <div key={f.id} className="space-y-2" data-testid={`bulk-cf-${f.id}`}>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">{f.name}</Label>
                    {editing && (
                      <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-500" onClick={() => setBulkFormData(p => { const cf = { ...p.custom_fields }; delete cf[f.id]; return { ...p, custom_fields: cf }; })}>
                        Annulla modifica
                      </Button>
                    )}
                  </div>
                  {!editing ? (
                    <Button variant="outline" size="sm" className="w-full text-gray-500" onClick={() => setBulkFormData(p => ({ ...p, custom_fields: { ...p.custom_fields, [f.id]: f.field_type === 'boolean' ? false : f.field_type === 'multiselect' ? [] : '' } }))} data-testid={`bulk-cf-enable-${f.id}`}>
                      Clicca per modificare {f.name}
                    </Button>
                  ) : (
                    <CustomFieldInput
                      field={f}
                      value={bulkFormData.custom_fields[f.id]}
                      onChange={(v) => setBulkFormData(p => ({ ...p, custom_fields: { ...p.custom_fields, [f.id]: v } }))}
                      testId={`bulk-cf-input-${f.id}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Annulla</Button>
            <Button
              onClick={handleBulkUpdate}
              disabled={bulkSaving || !bulkHasChanges}
              data-testid="bulk-update-sf-confirm"
            >
              {bulkSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Aggiorna {selectedIds.size} elementi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
